// GAP-15b — Bettor Archetype survey (OddsJam Wave 1).
//
// Personalisation, NOT a legal/safety gate: deliberately a standalone
// auth-gated route, NOT wired into AuthGate's mandatory onboarding
// machinery (steps.ts). That keeps the app-gating flow 100%
// untouched — no lockout risk, and the survey stays reachable (the
// "redirect away from enabled onboarding routes once gates pass"
// rule would otherwise make it unreachable).
//
// Asks the OddsJam spec's 3-archetype survey; resolves to STRIKZA's
// existing 4 (GAP-15a). Writes via the security-definer
// set_my_archetype RPC. SSR-pure (supabase resolved lazily).

import { useMemo, useState } from "react";

import {
  ARCHETYPE_SURVEY,
  resolveArchetype,
} from "../../onboarding/archetypeMapping.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

type SaveState = "idle" | "saving" | "saved" | "error";

const WRAP: React.CSSProperties = {
  maxWidth: 640,
  margin: "0 auto",
  padding: "32px 24px 80px",
  color: "var(--se-text, #fff)",
  fontFamily: "var(--se-font-body, system-ui)",
};

export function ArchetypeSurveyPage(): JSX.Element {
  const [answers, setAnswers] = useState<number[]>(
    () => ARCHETYPE_SURVEY.map(() => -1),
  );
  const [state, setState] = useState<SaveState>("idle");

  const complete = answers.every((a) => a >= 0);
  const resolved = useMemo(
    () => (complete ? resolveArchetype(answers) : null),
    [answers, complete],
  );

  async function submit(): Promise<void> {
    if (!resolved) return;
    setState("saving");
    try {
      const client = getBrowserClient();
      const { data, error } = await (client.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: unknown }>)(
        "set_my_archetype",
        { p_archetype: resolved.strikza },
      );
      setState(!error && data === true ? "saved" : "error");
    } catch {
      setState("error");
    }
  }

  return (
    <div data-testid="archetype-survey" style={WRAP}>
      <div
        style={{
          fontFamily: "var(--se-font-mono, monospace)",
          fontSize: 10,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: "var(--se-text-muted, #888)",
        }}
      >
        Personalise · optional
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 500, margin: "10px 0 6px" }}>
        Your bettor profile
      </h1>
      <p
        style={{
          fontSize: 13,
          color: "var(--se-text-sec, #aaa)",
          lineHeight: 1.55,
          margin: "0 0 26px",
        }}
      >
        Five quick questions. This tunes how STRIKZA frames intelligence
        for you — it never gates access and you can retake it anytime.
      </p>

      {ARCHETYPE_SURVEY.map((q, qi) => (
        <fieldset
          key={q.id}
          data-testid={`archetype-q-${q.id}`}
          style={{
            border: "1px solid var(--se-border, #2a2a2a)",
            borderRadius: 6,
            padding: "14px 16px",
            margin: "0 0 14px",
          }}
        >
          <legend style={{ fontSize: 13.5, padding: "0 6px" }}>
            {q.prompt}
          </legend>
          {q.options.map((opt, oi) => {
            const picked = answers[qi] === oi;
            return (
              <button
                key={opt.label}
                type="button"
                data-testid={`archetype-q-${q.id}-opt-${oi}`}
                aria-pressed={picked}
                onClick={() =>
                  setAnswers((prev) => {
                    const next = [...prev];
                    next[qi] = oi;
                    return next;
                  })}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  margin: "8px 0 0",
                  padding: "9px 12px",
                  borderRadius: 3,
                  border: `1px solid ${
                    picked ? "var(--se-amber, #d4a017)" : "var(--se-border, #2a2a2a)"
                  }`,
                  background: picked
                    ? "rgba(212,160,23,0.10)"
                    : "transparent",
                  color: "var(--se-text, #fff)",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </fieldset>
      ))}

      <button
        type="button"
        data-testid="archetype-submit"
        disabled={!complete || state === "saving" || state === "saved"}
        onClick={() => void submit()}
        style={{
          marginTop: 12,
          padding: "11px 22px",
          borderRadius: 2,
          border: "none",
          background: !complete || state === "saving"
            ? "var(--se-surface-raised, #222)"
            : "var(--se-amber, #d4a017)",
          color: !complete || state === "saving"
            ? "var(--se-text-muted, #888)"
            : "var(--se-surface-page, #000)",
          fontSize: 13.5,
          fontWeight: 600,
          cursor: complete && state === "idle" ? "pointer" : "default",
        }}
      >
        {state === "saving"
          ? "Saving…"
          : state === "saved"
            ? "Saved ✓"
            : "Save my profile"}
      </button>

      {state === "saved" && resolved ? (
        <div
          data-testid="archetype-result"
          style={{ marginTop: 14, fontSize: 13, color: "var(--se-text-sec, #aaa)" }}
        >
          Profile set: <strong>{resolved.strikza.replace(/_/g, " ")}</strong>.
          STRIKZA will frame intelligence to match.
        </div>
      ) : null}
      {state === "error" ? (
        <div
          data-testid="archetype-error"
          style={{ marginTop: 14, fontSize: 13, color: "var(--se-drop-red, #c0392b)" }}
        >
          Could not save — sign in and try again.
        </div>
      ) : null}
    </div>
  );
}
